import { useEffect, useState } from 'react'

/**
 * Detects whether the OS is currently drawing "classic" scrollbars —
 * persistent bars that take up layout space, which on macOS happens when a
 * mouse is connected — as opposed to "overlay" scrollbars that only appear
 * while scrolling (the default with a trackpad).
 *
 * Why this is needed: CSS cannot distinguish a mouse from a trackpad
 * ('pointer: fine' and pointer-event 'pointerType: mouse' both match each).
 * The OS scrollbar mode is the only reliable proxy, and it surfaces as the
 * measured width of a scrollable element.
 *
 * Why the shadow DOM: the app hides scrollbars globally
 * ('* { scrollbar-width: none }' and '::-webkit-scrollbar { width: 0 }'),
 * so a probe in the normal document would always measure 0. Those document
 * rules (a universal selector and a pseudo-element) do not apply inside a
 * shadow tree, so a probe there gets the real UA/OS scrollbar.
 */
const measureHasClassicScrollbars = (): boolean => {
  if (typeof document === 'undefined') return false

  const host = document.createElement('div')
  host.style.cssText =
    'position:absolute;top:-9999px;left:-9999px;width:100px;height:100px;opacity:0;pointer-events:none;'

  let hasClassic = false
  try {
    const shadow = host.attachShadow({ mode: 'open' })
    const probe = document.createElement('div')
    probe.style.cssText = 'width:100px;height:100px;overflow:scroll;'
    shadow.appendChild(probe)
    document.body.appendChild(host)

    // Overlay scrollbars take no layout width (0); classic ones take ~15px.
    hasClassic = probe.offsetWidth - probe.clientWidth > 0
  } catch {
    hasClassic = false
  } finally {
    if (host.parentNode) host.parentNode.removeChild(host)
  }

  return hasClassic
}

/**
 * Returns true while the OS is in classic-scrollbar mode (mouse attached),
 * false for overlay mode (trackpad/touch). Re-measures when the user might
 * have plugged or unplugged a mouse: on window focus and on resize (macOS
 * nudges layout when the scrollbar mode flips).
 */
export const useClassicScrollbars = (): boolean => {
  const [hasClassic, setHasClassic] = useState<boolean>(
    measureHasClassicScrollbars
  )

  useEffect(() => {
    const update = () => setHasClassic(measureHasClassicScrollbars())

    update()
    window.addEventListener('focus', update)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('focus', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  return hasClassic
}
